// Picture patterns (Year 2 pictures, 22 Oct 2026). Two kinds:
//
// - a repeating pattern: a row of shapes or coloured beads, with a gap shown
//   as an empty dashed box with "?" in it;
// - a growing pattern: steps side by side, each a tower or a row of things,
//   with "Step 1", "Step 2" and so on under it. One step may be "?".
//
// What goes in a gap, and how many things are in a step, are never written
// or spoken: working them out is the question.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export const PATTERN_SHAPES = [
  'circle',
  'square',
  'triangle',
  'star',
  'heart',
  'diamond',
  'rectangle',
] as const;

// Beads that differ in colour. Each colour also has its own marking, so
// colour is never the only way to tell them apart.
export const PATTERN_COLOURS = ['red', 'blue', 'yellow', 'green'] as const;

export const GROWING_ITEMS = ['block', 'star', 'counter'] as const;

export type PatternShape = (typeof PATTERN_SHAPES)[number];
export type PatternColour = (typeof PATTERN_COLOURS)[number];
export type PatternItem = PatternShape | PatternColour | '?';
export type GrowingItem = (typeof GROWING_ITEMS)[number];

export type RepeatingPatternSpec = {
  type: 'picture_pattern';
  kind: 'repeating';
  items: PatternItem[];
};

export type GrowingPatternSpec = {
  type: 'picture_pattern';
  kind: 'growing';
  // A tower stands up; a row lies along. Towers of blocks by default.
  layout: 'tower' | 'row';
  item: GrowingItem;
  // How many things are in each step, or "?" for the step to find.
  steps: (number | '?')[];
};

export type PicturePatternSpec = RepeatingPatternSpec | GrowingPatternSpec;

// More than this cannot be told apart in one row on a phone.
const MAX_ITEMS = 12;
const MAX_GAPS = 3;
const MAX_STEPS = 6;
const MAX_IN_STEP = 12;

function isWholeIn(value: unknown, low: number, high: number): boolean {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= low &&
    value <= high
  );
}

function isPatternItem(value: unknown): value is PatternItem {
  return (
    value === '?' ||
    (PATTERN_SHAPES as readonly unknown[]).includes(value) ||
    (PATTERN_COLOURS as readonly unknown[]).includes(value)
  );
}

export function isColour(item: PatternItem): item is PatternColour {
  return (PATTERN_COLOURS as readonly string[]).includes(item);
}

function readRepeating(items: unknown[]): RepeatingPatternSpec | null {
  if (items.length < 3 || items.length > MAX_ITEMS) return null;
  if (!items.every(isPatternItem)) return null;
  const gaps = items.filter((item) => item === '?').length;
  // Enough of the pattern must show for it to be found.
  if (gaps > MAX_GAPS || items.length - gaps < 2) return null;
  return { type: 'picture_pattern', kind: 'repeating', items };
}

function readGrowing(
  record: Record<string, unknown>,
  steps: unknown[],
): GrowingPatternSpec | null {
  const item = record.item ?? 'block';
  const layout = record.layout ?? 'tower';
  if (!(GROWING_ITEMS as readonly unknown[]).includes(item)) return null;
  if (layout !== 'tower' && layout !== 'row') return null;
  if (steps.length < 2 || steps.length > MAX_STEPS) return null;
  const read: (number | '?')[] = [];
  for (const step of steps) {
    if (step === '?') read.push('?');
    else if (isWholeIn(step, 1, MAX_IN_STEP)) read.push(step as number);
    else return null;
  }
  const gaps = read.filter((step) => step === '?').length;
  if (gaps > 1 || read.length - gaps < 2) return null;
  return {
    type: 'picture_pattern',
    kind: 'growing',
    layout,
    item: item as GrowingItem,
    steps: read,
  };
}

// A row of shapes (`items`), or steps that grow (`steps`): never both.
export function readPicturePattern(
  record: Record<string, unknown>,
): PicturePatternSpec | null {
  const { items, steps } = record;
  if (Array.isArray(items) === Array.isArray(steps)) return null;
  if (Array.isArray(items)) {
    if (record.item !== undefined || record.layout !== undefined) return null;
    return readRepeating(items as unknown[]);
  }
  return readGrowing(record, steps as unknown[]);
}

const PLURAL: Record<GrowingItem, string> = {
  block: 'blocks',
  star: 'stars',
  counter: 'counters',
};

function sayItem(item: PatternItem): string {
  if (item === '?') return 'a gap';
  return isColour(item) ? `${item} bead` : item;
}

// What a screen reader is told in place of the picture: the shapes in the
// row and where the gaps are, or how many steps there are and which is "?".
// Never what goes in a gap, and never how many things are in a step.
export function describePicturePattern(spec: PicturePatternSpec): string {
  if (spec.kind === 'repeating') {
    return `A pattern in a row: ${spec.items.map(sayItem).join(', ')}.`;
  }
  const what =
    spec.layout === 'tower'
      ? `a tower of ${PLURAL[spec.item]}`
      : `a row of ${PLURAL[spec.item]}`;
  const unknown = spec.steps.indexOf('?');
  const gap =
    unknown < 0 ? '' : ` Step ${String(unknown + 1)} is a question mark.`;
  return `A growing pattern in ${String(spec.steps.length)} steps. Each step is ${what}.${gap}`;
}

// What follows is where everything is drawn. The units are close to one
// pixel each when the picture is shown at its natural width.

export const PATTERN_LABEL_SIZE = 12;
const EDGE = 8;
const MAX_WIDTH = 340;

export type RepeatingPicture = {
  items: { item: PatternItem; x: number; y: number }[];
  size: number;
  width: number;
  height: number;
};

export function repeatingPicture(spec: RepeatingPatternSpec): RepeatingPicture {
  const gap = 6;
  const count = spec.items.length;
  const size = Math.min(
    40,
    Math.floor((MAX_WIDTH - EDGE * 2 - (count - 1) * gap) / count),
  );
  return {
    items: spec.items.map((item, index) => ({
      item,
      x: EDGE + index * (size + gap),
      y: EDGE,
    })),
    size,
    width: EDGE * 2 + count * size + (count - 1) * gap,
    height: EDGE * 2 + size,
  };
}

export type GrowingStep = {
  // The step number printed under it, from 1.
  number: number;
  // Each thing in the step; empty for the "?" step.
  things: { x: number; y: number }[];
  // The dashed box drawn for the "?" step.
  gap: { x: number; y: number; width: number; height: number } | null;
  label: { x: number; y: number };
};

export type GrowingPicture = {
  steps: GrowingStep[];
  size: number;
  width: number;
  height: number;
};

// Things sit in fives, with a bigger gap after each five, so they are easy
// to count.
function offset(index: number, step: number): number {
  return index * step + Math.floor(index / 5) * 6;
}

function run(count: number, step: number): number {
  return count < 1 ? 0 : offset(count - 1, step) + step - 2;
}

export function growingPicture(spec: GrowingPatternSpec): GrowingPicture {
  const most = Math.max(...spec.steps.map((step) => (step === '?' ? 0 : step)));
  if (spec.layout === 'tower') {
    const size = 22;
    const step = size + 2;
    const column = 52;
    const tallest = Math.max(run(most, step), 40);
    const base = EDGE + tallest;
    return {
      steps: spec.steps.map((count, index) => {
        const middle = EDGE + index * column + column / 2;
        return {
          number: index + 1,
          things:
            count === '?'
              ? []
              : Array.from({ length: count }, (_, at) => ({
                  x: middle - size / 2,
                  y: base - size - offset(at, step),
                })),
          gap:
            count === '?'
              ? { x: middle - 18, y: base - 40, width: 36, height: 40 }
              : null,
          label: { x: middle, y: base + 18 },
        };
      }),
      size,
      width: EDGE * 2 + spec.steps.length * column,
      height: base + 26,
    };
  }
  const size = 18;
  const step = size + 3;
  const labelWidth = 50;
  const line = 30;
  const left = EDGE + labelWidth;
  return {
    steps: spec.steps.map((count, index) => {
      const top = EDGE + index * line;
      return {
        number: index + 1,
        things:
          count === '?'
            ? []
            : Array.from({ length: count }, (_, at) => ({
                x: left + offset(at, step),
                y: top + (line - size) / 2 - 2,
              })),
        gap:
          count === '?'
            ? { x: left, y: top, width: 60, height: line - 6 }
            : null,
        label: { x: EDGE, y: top + line / 2 - 2 },
      };
    }),
    size,
    width: left + Math.max(run(most, step), 60) + EDGE,
    height: EDGE * 2 + spec.steps.length * line - 6,
  };
}
